import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';
import {
  START_RATING,
  newId,
  schaakId,
  type CardMap,
  type CardProgress,
  type DayLog,
  type SchaakMutation,
  type WoodpeckerCycle,
} from '@familyhub/shared';
import { stamp } from '../platform/clock';
import { registerCleanup } from '../platform/cleanup';
import {
  sdb,
  type AccountsRecord,
  type CardRecord,
  type GameRecord,
  type MistakeRecord,
  type ProfileRecord,
  type WoodpeckerRecord,
} from './db';
import { schaakSync } from './sync';

/**
 * The schaak write path: apply locally, queue the mutation, let the runner
 * replay it — the route every module's writes take.
 *
 * The profile (rating) and the current Woodpecker cycle are read-modify-writes
 * on a row touched every puzzle, which is the case CLAUDE.md warns about: a
 * Dexie write is not reliably visible to the next read. So the last row this
 * phone wrote is held in memory (`written`) and every read overlays it, newest
 * `updated_at` winning — Morse's remedy, and the list's `readRow`.
 */

type Row = CardRecord | ProfileRecord | WoodpeckerRecord | AccountsRecord;
const written = new Map<string, Row>();
registerCleanup(async () => written.clear());

function newer<T extends Row>(a: T | undefined, b: T | undefined): T | undefined {
  if (!a) return b;
  if (!b) return a;
  return a.updated_at >= b.updated_at ? a : b;
}

function overlay<T extends Row>(rows: T[], userId: string): T[] {
  const byId = new Map<string, T>();
  for (const r of rows) byId.set(r.id, r);
  for (const r of written.values()) {
    const t = r as T;
    if (t.created_by === userId && byId.has(t.id)) byId.set(t.id, newer(byId.get(t.id), t)!);
  }
  return [...byId.values()].filter((r) => !r.deleted_at);
}

export interface SchaakState {
  cards: CardMap;
  rating: number;
  ratingGames: number;
  /** Day by day: rating, attempted, solved. */
  days: DayLog[];
  cycles: WoodpeckerCycle[];
  accounts: { lichess: string | null; chesscom: string | null };
  /** His imported games, newest first, and the mistakes found in them. */
  games: GameRecord[];
  mistakes: MistakeRecord[];
}

/**
 * This learner's whole state. Undefined while Dexie loads *or the session has
 * not resolved*; tagged with the user it was read for, because `useLiveQuery`
 * returns the previous query's answer while a new one loads (the Morse lesson —
 * an empty pre-session read must never pass for "nothing done yet").
 */
export function useSchaak(userId: string | null): SchaakState | undefined {
  const read = useLiveQuery(
    async () => ({
      userId,
      cards: userId ? await sdb.cards.where('created_by').equals(userId).toArray() : [],
      profile: userId ? await sdb.profile.where('created_by').equals(userId).toArray() : [],
      cycles: userId ? await sdb.woodpecker.where('created_by').equals(userId).toArray() : [],
      accounts: userId ? await sdb.accounts.where('created_by').equals(userId).toArray() : [],
      games: userId ? await sdb.games.where('user_id').equals(userId).toArray() : [],
      mistakes: userId ? await sdb.mistakes.where('user_id').equals(userId).toArray() : [],
    }),
    [userId],
  );
  return useMemo(() => {
    if (!read || !userId || read.userId !== userId) return undefined;
    // Overlay rows this phone wrote that Dexie has not shown yet, too.
    const mine = <T extends Row>(rows: T[], prefix: (r: Row) => boolean) => {
      const extra = [...written.values()].filter((r) => r.created_by === userId && prefix(r)) as T[];
      const ids = new Set(rows.map((r) => r.id));
      return overlay([...rows, ...extra.filter((r) => !ids.has(r.id))], userId);
    };
    const cards: Record<string, CardProgress> = {};
    for (const c of mine(read.cards, (r) => 'ref' in r)) cards[c.ref] = toCard(c);
    const profile = mine(read.profile, (r) => 'rating' in r)[0];
    const cycles = mine(read.cycles, (r) => 'cycle' in r).map(toCycle);
    const acc = mine(read.accounts, (r) => 'lichess_username' in r)[0];
    return {
      accounts: { lichess: acc?.lichess_username ?? null, chesscom: acc?.chesscom_username ?? null },
      games: read.games.filter((g) => !g.deleted_at).sort((a, b) => (a.played_at < b.played_at ? 1 : -1)),
      mistakes: read.mistakes.filter((m) => !m.deleted_at),
      cards,
      rating: profile?.rating ?? START_RATING,
      ratingGames: profile?.rating_games ?? 0,
      days: profile?.days ?? [],
      cycles,
    };
  }, [read, userId]);
}

const toCard = (r: CardRecord): CardProgress => ({
  kind: r.kind,
  ref: r.ref,
  theme: r.theme,
  box: r.box,
  due_at: r.due_at,
  attempts: r.attempts,
  solved: r.solved,
  failed: r.failed,
  first_result: r.first_result,
  last_at: r.last_at,
  reason: r.reason ?? null,
});

const toCycle = (r: WoodpeckerRecord): WoodpeckerCycle => ({
  set_no: r.set_no,
  cycle: r.cycle,
  started_at: r.started_at,
  finished_at: r.finished_at,
  position: r.position,
  correct: r.correct,
  seconds: r.seconds,
});

async function write(
  table: 'schaak_cards' | 'schaak_profile' | 'schaak_woodpecker' | 'schaak_accounts',
  store: typeof sdb.cards | typeof sdb.profile | typeof sdb.woodpecker | typeof sdb.accounts,
  record: Row,
): Promise<void> {
  written.set(record.id, record);
  const mutation = { op: 'upsert', table, id: newId(), row: record } as unknown as SchaakMutation;
  await sdb.transaction('rw', [store, sdb.outbox], async () => {
    await (store as typeof sdb.cards).put(record as CardRecord);
    await sdb.outbox.add({ mutation, queued_at: new Date().toISOString() });
  });
  void schaakSync.sync();
}

async function previous<T extends Row>(store: { get(id: string): Promise<T | undefined> }, id: string) {
  return newer(await store.get(id), written.get(id) as T | undefined);
}

export async function saveCard(userId: string, c: CardProgress): Promise<void> {
  const id = schaakId(userId, c.kind, c.ref);
  const prev = await previous(sdb.cards, id);
  const record: CardRecord = {
    id,
    ...c,
    created_by: userId,
    created_at: prev?.created_at,
    updated_at: stamp(),
    deleted_at: null,
  };
  await write('schaak_cards', sdb.cards, record);
}

export async function saveProfile(userId: string, rating: number, games: number, days: DayLog[]): Promise<void> {
  const id = schaakId(userId, 'profile');
  const prev = await previous(sdb.profile, id);
  const record: ProfileRecord = {
    id,
    rating,
    rating_games: games,
    days,
    created_by: userId,
    created_at: prev?.created_at,
    updated_at: stamp(),
    deleted_at: null,
  };
  await write('schaak_profile', sdb.profile, record);
}

export async function saveCycle(userId: string, c: WoodpeckerCycle): Promise<void> {
  const id = schaakId(userId, 'woodpecker', String(c.set_no), String(c.cycle));
  const prev = await previous(sdb.woodpecker, id);
  const record: WoodpeckerRecord = {
    id,
    ...c,
    created_by: userId,
    created_at: prev?.created_at,
    updated_at: stamp(),
    deleted_at: null,
  };
  await write('schaak_woodpecker', sdb.woodpecker, record);
}

/** Which Lichess / Chess.com accounts to import from; empty clears one. */
export async function saveAccounts(userId: string, lichess: string | null, chesscom: string | null): Promise<void> {
  const id = schaakId(userId, 'accounts');
  const prev = await previous(sdb.accounts, id);
  const record: AccountsRecord = {
    id,
    lichess_username: lichess,
    chesscom_username: chesscom,
    created_by: userId,
    created_at: prev?.created_at,
    updated_at: stamp(),
    deleted_at: null,
  };
  await write('schaak_accounts', sdb.accounts, record);
}
